// @MX:ANCHOR: [AUTO] ETL 스케줄 설정 GET/PATCH/즉시실행 composable — SyncTogglePanel 단독 의존
// @MX:REASON: [AUTO] getConfig/updateConfig/triggerGtinSync/triggerSerialLotSync 4 API 집중, optimistic lock 추적
// @MX:SPEC: SPEC-ECOUNT-ETL-CRON-CONSOLE-001 §3.3 Phase β

import { reactive, ref } from 'vue';
import { getConfig, updateConfig, triggerGtinSync, triggerSerialLotSync } from '../../../apis/ecountEtl.js';

/**
 * ECOUNT ETL 스케줄 설정 composable
 *
 * @returns {Object} state, loading, error, fetch, patchConfig, runGtinSync, runSerialLotSync
 */
export function useSyncConfig() {
  // 서버 응답 13 필드 + optimistic lock 버전
  const state = reactive({
    masterEnabled: false,
    gtinEnabled: false,
    gtinCronExpr: null,
    gtinLastSyncAt: null,
    gtinNextSyncAt: null,
    serialLotEnabled: false,
    serialLotCronExpr: null,
    serialLotLastSyncAt: null,
    serialLotNextSyncAt: null,
    slackEnabled: false,
    slackWebhookConfigured: false,
    createdAt: null,
    updatedAt: null,
    // @MX:NOTE: [AUTO] optimistic lock 버전 — PATCH body에 expectedVersion으로 동봉
    version: 0,
  });

  const loading = ref(false);
  const error = ref(null);
  // 즉시 실행 버튼별 loading 상태
  const triggerLoadingGtin = ref(false);
  const triggerLoadingSerial = ref(false);

  /**
   * GET /ecount-etl/config — 13 필드 로드 + version 추적
   */
  async function fetch() {
    loading.value = true;
    error.value = null;
    try {
      const res = await getConfig();
      applyServerState(res.data);
    } catch (e) {
      error.value = e?.response?.data?.message || '설정 로드 실패';
    } finally {
      loading.value = false;
    }
  }

  /**
   * PATCH /ecount-etl/config — optimistic lock 포함
   *
   * @param {Object} patch - 변경할 필드 (gtinEnabled, serialLotEnabled 등)
   * @returns {{ success: boolean, slackNotified: boolean, conflict: boolean }}
   */
  async function patchConfig(patch) {
    loading.value = true;
    error.value = null;
    try {
      const res = await updateConfig({
        ...patch,
        // @MX:NOTE: [AUTO] optimistic lock — 서버가 409 시 currentVersion + currentConfig 반환
        expectedVersion: state.version,
      });
      const data = res.data;
      applyServerState(data);
      return {
        success: true,
        // @MX:NOTE: [AUTO] D-20 — slackNotified=false 시 caller가 warning toast 노출
        slackNotified: data.slackNotified !== false,
      };
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        // @MX:NOTE: [AUTO] N-2/N-3 흡수 — 409 conflict 시 server-driven 자동 갱신
        const conflictData = e.response.data;
        if (conflictData?.currentConfig) {
          applyServerState({
            ...conflictData.currentConfig,
            version: conflictData.currentVersion,
          });
        }
        return { success: false, conflict: true, slackNotified: true };
      }
      error.value = e?.response?.data?.message || '설정 저장 실패';
      return { success: false, conflict: false, slackNotified: true };
    } finally {
      loading.value = false;
    }
  }

  /**
   * POST /ecount-etl/admin/sync-trigger (GTIN)
   *
   * @returns {{ success: boolean, loginFail: boolean, runId: string|null, message: string }}
   */
  async function runGtinSync() {
    triggerLoadingGtin.value = true;
    try {
      const res = await triggerGtinSync();
      const data = res.data;
      // @MX:NOTE: [AUTO] D-19 — code=203 시 LOGIN_FAIL. caller가 빨간 toast 노출.
      if (data?.code === 203) {
        return { success: false, loginFail: true, runId: null, message: data.message || 'ECOUNT API 키 무효' };
      }
      return { success: true, loginFail: false, runId: data?.runId ?? null, message: '' };
    } catch (e) {
      const data = e?.response?.data;
      if (data?.code === 203) {
        return { success: false, loginFail: true, runId: null, message: data.message || 'ECOUNT API 키 무효' };
      }
      return { success: false, loginFail: false, runId: null, message: e?.response?.data?.message || '즉시 실행 실패' };
    } finally {
      triggerLoadingGtin.value = false;
    }
  }

  /**
   * POST /ecount-etl/admin/sync-trigger (Serial-Lot)
   *
   * @returns {{ success: boolean, loginFail: boolean, runId: string|null, message: string }}
   */
  async function runSerialLotSync() {
    triggerLoadingSerial.value = true;
    try {
      const res = await triggerSerialLotSync();
      const data = res.data;
      if (data?.code === 203) {
        return { success: false, loginFail: true, runId: null, message: data.message || 'ECOUNT API 키 무효' };
      }
      return { success: true, loginFail: false, runId: data?.runId ?? null, message: '' };
    } catch (e) {
      const data = e?.response?.data;
      if (data?.code === 203) {
        return { success: false, loginFail: true, runId: null, message: data.message || 'ECOUNT API 키 무효' };
      }
      return { success: false, loginFail: false, runId: null, message: e?.response?.data?.message || '즉시 실행 실패' };
    } finally {
      triggerLoadingSerial.value = false;
    }
  }

  /**
   * 서버 응답을 reactive state에 적용 (내부 헬퍼)
   * @param {Object} data - 서버 응답 데이터
   */
  function applyServerState(data) {
    if (!data) return;
    state.masterEnabled        = data.masterEnabled        ?? false;
    state.gtinEnabled          = data.gtinEnabled          ?? false;
    state.gtinCronExpr         = data.gtinCronExpr         ?? null;
    state.gtinLastSyncAt       = data.gtinLastSyncAt       ?? null;
    state.gtinNextSyncAt       = data.gtinNextSyncAt       ?? null;
    state.serialLotEnabled     = data.serialLotEnabled     ?? false;
    state.serialLotCronExpr    = data.serialLotCronExpr    ?? null;
    state.serialLotLastSyncAt  = data.serialLotLastSyncAt  ?? null;
    state.serialLotNextSyncAt  = data.serialLotNextSyncAt  ?? null;
    state.slackEnabled         = data.slackEnabled         ?? false;
    state.slackWebhookConfigured = data.slackWebhookConfigured ?? false;
    state.createdAt            = data.createdAt            ?? null;
    state.updatedAt            = data.updatedAt            ?? null;
    state.version              = data.version              ?? 0;
  }

  return {
    state,
    loading,
    error,
    triggerLoadingGtin,
    triggerLoadingSerial,
    fetch,
    patchConfig,
    runGtinSync,
    runSerialLotSync,
  };
}
